export type QueueModeSound = "launch" | "work" | "skip" | "snooze" | "close" | "handoff" | "submit"

const MELODIES: Record<QueueModeSound, readonly number[]> = {
    launch: [262, 330, 392, 523, 659, 784],
    work: [988, 1319],
    skip: [784, 523, 659],
    snooze: [659, 523, 392],
    close: [523, 659, 784, 1047],
    handoff: [392, 587, 784],
    submit: [523, 659, 784, 1319],
}

export class QueueModeSounds {
    private context: BaseAudioContext | null = null
    private voices = new Set<OscillatorNode>()
    private generation = 0
    private reportedFailure = false

    constructor(
        private readonly createContext: () => BaseAudioContext | null = () =>
            typeof AudioContext === "undefined" || navigator.userActivation?.hasBeenActive === false ? null : new AudioContext({ latencyHint: "interactive" })
    ) {}

    async prepare(): Promise<BaseAudioContext | null> {
        try {
            this.context ??= this.createContext()
            const context = this.context
            if (typeof AudioContext !== "undefined" && context instanceof AudioContext && context.state !== "running") await context.resume()
            return context
        } catch (error) {
            this.reportFailure(error)
            return null
        }
    }

    async play(sound: QueueModeSound): Promise<void> {
        this.cancelVoices()
        const generation = this.generation
        const requestedAtMs = Date.now()
        const context = await this.prepare()
        // Autoplay recovery must never replay an old cue after mute, pause, or a later gesture.
        if (!context || context !== this.context || generation !== this.generation || Date.now() - requestedAtMs > 250) return

        try {
            const notes = MELODIES[sound]
            notes.forEach((frequency, index) => {
                const oscillator = context.createOscillator()
                const envelope = context.createGain()
                const start = context.currentTime + index * 0.075
                const duration = index === notes.length - 1 ? 0.18 : 0.07
                oscillator.type = sound === "snooze" ? "triangle" : "square"
                oscillator.frequency.value = frequency
                envelope.gain.setValueAtTime(0, start)
                envelope.gain.linearRampToValueAtTime(0.035, start + 0.005)
                envelope.gain.exponentialRampToValueAtTime(0.001, start + duration - 0.01)
                envelope.gain.linearRampToValueAtTime(0, start + duration)
                oscillator.connect(envelope)
                envelope.connect(context.destination)
                oscillator.onended = () => {
                    oscillator.disconnect()
                    envelope.disconnect()
                    this.voices.delete(oscillator)
                }
                oscillator.start(start)
                oscillator.stop(start + duration)
                this.voices.add(oscillator)
            })
        } catch (error) {
            this.cancelVoices()
            this.reportFailure(error)
        }
    }

    dispose(): void {
        this.cancelVoices()
        const context = this.context
        this.context = null
        if (typeof AudioContext !== "undefined" && context instanceof AudioContext && context.state !== "closed") {
            void context.close().catch((error: unknown) => this.reportFailure(error))
        }
    }

    private cancelVoices(): void {
        this.generation += 1
        for (const oscillator of this.voices) {
            oscillator.stop()
            oscillator.disconnect()
        }
        this.voices.clear()
    }

    private reportFailure(error: unknown): void {
        if (this.reportedFailure) return
        this.reportedFailure = true
        console.warn("[QueueModeSounds] Audio unavailable; Q mode remains usable", { error })
    }
}
